import { cn } from "@tcg/ui";
import { CheckCircle2, FileText, Minus, Plus, Printer, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";
import { Modal } from "@/components/Modal";
import { call } from "@/lib/api";
import { ApiError, errorText } from "@/lib/errors";
import { newIdempotencyKey } from "@/lib/idempotency";
import { isPrinterError } from "@/lib/printer";
import type { LabelJob, LabelJobCreate, LabelSize } from "@/lib/types";

export type LabelSource =
  | { variantIds: string[] }
  | { unitCodes: string[] }
  | { buylistIntakeId: string }
  | { consignmentIntakeId: string }
  | { shelves: NonNullable<LabelJobCreate["shelves"]> };

type Result =
  | { kind: "idle" }
  | { kind: "printed"; job: LabelJob }
  | { kind: "pdf"; job: LabelJob; url: string }
  | { kind: "failed"; message: string; job?: LabelJob };

/** Fetch the rendered labels (ZPL / TSPL text or the PDF) of a job. */
export async function labelOutput(
  api: ReturnType<typeof usePos>["api"],
  jobId: string,
): Promise<Blob> {
  const response = await api.GET("/v1/pos/label-jobs/{job_id}/output", {
    params: { path: { job_id: jobId } },
    parseAs: "blob",
  });
  if (!response.response.ok || !(response.data instanceof Blob)) {
    throw ApiError.fromEnvelope(response.response.status, response.error);
  }
  return response.data;
}

async function blobBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === "function") {
    return new Uint8Array(await blob.arrayBuffer());
  }
  return new Uint8Array(
    await new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error ?? new Error("read failed"));
      reader.readAsArrayBuffer(blob);
    }),
  );
}

/**
 * 「ラベル印刷」 (T07-34, T07-16): how many copies, which size → pos-service renders the labels
 * (`POST /pos/label-jobs` — barcode, price 税込, Rank A–D / 「PSA 10」, 「委託」) → the bytes go to
 * the label printer of this tablet, or the PDF opens for an ordinary printer. Used after 買取 /
 * 委託受付, from 設定 (a card scanned → a new label) and for the test label.
 */
export interface LabelPrintModalProps {
  open: boolean;
  onClose: () => void;
  source: LabelSource | null;
  title?: string;
  defaultCopies?: number;
}

export function LabelPrintModal(props: LabelPrintModalProps) {
  // a fresh dialog every time it opens (size / copies from the settings, no old result)
  return props.open ? <LabelPrintDialog {...props} /> : null;
}

function LabelPrintDialog({
  open,
  onClose,
  source,
  title,
  defaultCopies = 1,
}: LabelPrintModalProps) {
  const { t } = useTranslation();
  const { api, labels } = usePos();
  const [size, setSize] = useState<LabelSize>(labels.config.size);
  const [copies, setCopies] = useState(defaultCopies);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result>({ kind: "idle" });

  useEffect(
    () => () => {
      if (result.kind === "pdf") {
        URL.revokeObjectURL(result.url);
      }
    },
    [result],
  );

  const print = async () => {
    if (!source) {
      return;
    }
    setBusy(true);
    setResult({ kind: "idle" });
    const format = labels.format;
    try {
      const job = await call(() =>
        api.POST("/v1/pos/label-jobs", {
          body: {
            ...source,
            type: "shelves" in source ? "shelf" : "price_unit",
            format,
            size,
            mode: "auto",
            copies,
          },
          headers: { "Idempotency-Key": newIdempotencyKey() },
        }),
      );
      if (job.status !== "ready" || job.count === 0) {
        setResult({
          kind: "failed",
          job,
          message: job.missing?.length ? t("labels.all_missing") : t("labels.failed"),
        });
        return;
      }
      const blob = await labelOutput(api, job.id);
      if (format === "pdf") {
        const url = URL.createObjectURL(
          blob.type ? blob : new Blob([blob], { type: "application/pdf" }),
        );
        window.open(url, "_blank", "noopener");
        setResult({ kind: "pdf", job, url });
      } else {
        await labels.print(await blobBytes(blob));
        setResult({ kind: "printed", job });
      }
    } catch (error) {
      setResult({
        kind: "failed",
        message: isPrinterError(error) ? t(`print.${error.code}`) : errorText(error, t),
      });
    } finally {
      setBusy(false);
    }
  };

  const job = "job" in result ? result.job : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={busy}
      size="lg"
      title={title ?? t("labels.title")}
      description={t("labels.description", {
        printer: t(`labels.connection_${labels.config.connection}`),
      })}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="min-h-pos-touch rounded-md border px-6 text-lg"
          >
            {t("app.close")}
          </button>
          <button
            type="button"
            onClick={() => void print()}
            disabled={busy || !source}
            className="flex min-h-pos-touch items-center gap-2 rounded-md bg-primary px-8 text-lg font-semibold text-primary-foreground disabled:opacity-50"
          >
            <Printer aria-hidden className="size-5" />
            {busy ? t("app.saving") : t("labels.print")}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <fieldset className="flex flex-wrap gap-2">
          <legend className="mb-1 font-medium">{t("labels.size")}</legend>
          {(["40x30", "58x40"] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={size === value}
              onClick={() => setSize(value)}
              className={cn(
                "min-h-pos-touch rounded-md border px-6 text-lg",
                size === value && "border-primary bg-primary text-primary-foreground",
              )}
            >
              {value.replace("x", " × ")} mm
            </button>
          ))}
        </fieldset>
        <div role="group" aria-label={t("labels.copies")} className="flex items-center gap-3">
          <span className="font-medium">{t("labels.copies")}</span>
          <button
            type="button"
            aria-label={t("labels.fewer")}
            onClick={() => setCopies((c) => Math.max(1, c - 1))}
            className="flex size-pos-touch items-center justify-center rounded-md border"
          >
            <Minus aria-hidden className="size-6" />
          </button>
          <output className="w-12 text-center text-2xl font-semibold tabular-nums">{copies}</output>
          <button
            type="button"
            aria-label={t("labels.more")}
            onClick={() => setCopies((c) => Math.min(50, c + 1))}
            className="flex size-pos-touch items-center justify-center rounded-md border"
          >
            <Plus aria-hidden className="size-6" />
          </button>
        </div>
        {job?.labels?.length ? (
          <ul aria-label={t("labels.preview")} className="flex flex-col gap-1">
            {job.labels.map((label, index) => (
              <li
                key={`${label.sku}-${String(index)}`}
                className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2"
              >
                <strong>{label.title.ja ?? label.title.en ?? label.sku}</strong>
                <span>{label.conditionLine || label.condition}</span>
                {label.consignedMark ? <span>{label.consignedMark}</span> : null}
                <span className="ml-auto tabular-nums">{label.priceText}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {job?.missing?.length ? (
          <p className="text-pos-problem">
            {t("labels.missing", { count: job.missing.length, codes: job.missing.join(", ") })}
          </p>
        ) : null}
        {result.kind === "printed" ? (
          <p role="status" className="flex items-center gap-2 text-lg text-pos-ok">
            <CheckCircle2 aria-hidden className="size-6" />
            {t("labels.printed", { count: result.job.count })}
          </p>
        ) : null}
        {result.kind === "pdf" ? (
          <p role="status" className="flex items-center gap-2 text-lg">
            <FileText aria-hidden className="size-6" />
            <a href={result.url} target="_blank" rel="noopener" className="underline">
              {t("labels.open_pdf", { count: result.job.count })}
            </a>
          </p>
        ) : null}
        {result.kind === "failed" ? (
          <p role="alert" className="flex items-center gap-2 text-lg text-pos-problem">
            <TriangleAlert aria-hidden className="size-6" />
            {result.message}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
